import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Icon, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { HtmlTooltip } from '../../html-tooltip/HtmlToolTip';
import { IListagemTipo, RenderTree, TipoService } from '../../../services/api/tipo/TipoService';
import { Environment } from '../../../environment';
import { AlertSnackBar } from '../../alert/AlertSnackBar';

interface IListagemTiposProps {
    /**
     * Bolean que indica se o model deve ser aberto ou fechado
     * */
    abrir?: boolean;
    /**
     * Bolean que indica se os tipos estão sendo carregados
     */
    // isLoading: boolean;
    /**
     * Id do setor para listar os tipos relacionados a ele.
     */
    idSetor: number;
    /**
     * Índice da caixa para listar os tipos relacionados a ela, caso queira controlar os tipos listados por caixa. 
     */
    indiceCaixa?: boolean;
    /**
     * Função para consultar por idTipo
     * */
    handleConsultarPorIdTipo?: (consultarPorIdTipo: boolean) => void;
    /**
     * Id do tipo selecionado
     */
    setIdTipo: (id: number) => void;
    /**
     * Função para setar os tipos listados, caso queira controlar os tipos listados externamente.
     */
    setRowsTipo?: (rows: IListagemTipo[]) => void;
    /**
     * Texto do tooltip para quando não encontrar algum tipo
     */
    txtTooltip: string;
    /**
     * Função ao confirmar
     * */
    AoConfirmar: () => void;
    /**
     * Função ao cancelar
     * */
    AoCancelar: () => void;
}


export const ListagemTipos: React.FC<IListagemTiposProps> = ({
    abrir = false,
    handleConsultarPorIdTipo,
    setIdTipo,
    setRowsTipo,
    idSetor,
    indiceCaixa = false,
    txtTooltip,
    AoConfirmar,
    AoCancelar
}) => {
    const [tipos, setTipos] = useState<IListagemTipo[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    const [alertSnackbar, setAlertSnackbar] = useState<{ tipoAlert: string, mensagem: string } | undefined>(undefined);
    const [duracaoAlert, setDuracaoAlert] = useState<number>(Environment.DURACAO_ALERT + 500);

    useEffect(() => {
        if (idSetor !== undefined && idSetor !== null && idSetor !== 0) {
            setTipos([]);
            setIsLoading(true);
            TipoService.getByIdSetor(idSetor, indiceCaixa).then((result) => {
                setIsLoading(false);

                if (result instanceof Error) {
                    setAlertSnackbar({ tipoAlert: "erro", mensagem: result.message });
                    setTimeout(() => {
                        setAlertSnackbar(undefined);
                    }, duracaoAlert);
                    throw new Error(result.message);
                }
                else {
                    setTipos(result);
                    if (result.length === 1 && result[0].idTipo !== undefined && result[0].idTipo !== null && result[0].tipos === undefined)
                        {
                            handleSelectTipo({} as React.SyntheticEvent, result[0].idTipo.toString());
                        }
                        setRowsTipo !== undefined && setRowsTipo(result);
                }
            });
        } else {
            setTipos([]);
            setSelectedIdTipo(0);
        }
    }, [idSetor]);


    const [selectedIdTipo, setSelectedIdTipo] = useState<number>(0);
    const [expanded, setExpanded] = useState<string[]>([]);

    const handleSelectTipo = (event: React.SyntheticEvent, nodeId: string) => {
        setIdTipo(Number(nodeId));
        setSelectedIdTipo(Number(nodeId));
    };

    const handleToggleTipo = (event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };

    const renderTree = (nodes: RenderTree) => (
        <TreeItem key={nodes.idTipo} nodeId={(nodes.idTipo).toString()} label={nodes.nomeTipo}>
            {Array.isArray(nodes.tipos)
                ? nodes.tipos.map((node) => renderTree(node))
                : null}
        </TreeItem>
    );    

return (
    <>
        <Dialog
            open={abrir}
            onClose={AoCancelar}
            >
            <DialogTitle id="scroll-dialog-title"> Tipos </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress />
                    </Box>
                ) : (
                    <TreeView
                        defaultCollapseIcon={<ExpandMoreIcon />}
                        defaultExpandIcon={<ChevronRightIcon />}
                        sx={{ width: 500, minHeight: 200, flexGrow: 1 }}
                        
                        selected={selectedIdTipo.toString()}
                        onNodeSelect={handleSelectTipo}
                        
                        expanded={expanded}
                        onNodeToggle={handleToggleTipo}
                    >
                        {tipos.map(tipo => renderTree(tipo))}
                    </TreeView>
                )}

                <HtmlTooltip arrow title={
                    <React.Fragment>
                        {txtTooltip}
                    </React.Fragment>
                }>
                    <Typography variant='caption' > <u> Não encontrou algum tipo? </u> </Typography>
                </HtmlTooltip>

            </DialogContent>
            <DialogActions>
                <Box
                    width="100%"
                    marginX={1}
                    padding={1}
                    paddingX={2}
                    gap={1}
                    display="flex"
                    justifyContent="center"
                    >
                    <Button variant="contained" color="error" startIcon={<Icon>close</Icon>} onClick={AoCancelar}>
                        <Typography variant='button' noWrap> Fechar </Typography>
                    </Button>
                    {handleConsultarPorIdTipo === null || handleConsultarPorIdTipo === undefined ? false : (
                        <Button variant="contained" color="warning" startIcon={<Icon>search</Icon>} onClick={() => handleConsultarPorIdTipo(false)} disabled={isLoading || selectedIdTipo === 0}>
                            <Typography variant='button' noWrap> Consultar por tipo </Typography>
                        </Button>
                    )}
                    <Button variant="contained" startIcon={<Icon>check</Icon>} onClick={AoConfirmar} disabled={isLoading || selectedIdTipo === 0}>
                        <Typography variant='button' noWrap> Confirmar </Typography>
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
                    
        {alertSnackbar !== undefined && (
        <>
            {alertSnackbar.tipoAlert === "sucesso" && (
            <AlertSnackBar gravidade="success" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
            )}
            {alertSnackbar.tipoAlert === "erro" && (
            <AlertSnackBar gravidade="error" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
            )}
            {alertSnackbar.tipoAlert === "aviso" && (
            <AlertSnackBar gravidade="warning" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
            )}
        </>
        )}
    </>

)
}